import { FC, ReactNode, useState } from 'react';
import { Button, SxProps, Theme, useTheme } from '@mui/material';

import { palette } from '../theme';
export enum GenericButtonTypeEnum {
  Primary = 'primary',
  Secondary = 'secondary',
  Delete = 'delete',
  Neutral = 'neutral'
}
interface GenericButtonProps {
  label: string;
  variant?: 'text' | 'outlined' | 'contained';
  buttonType?: GenericButtonTypeEnum;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  isActive?: boolean;
  onClick?: () => void;
  onAsyncAction?: () => Promise<void>;
  disabled?: boolean;
  sx?: SxProps;
  isLoading?: boolean;
  type?: 'button' | 'submit' | 'reset';
}

const GenericButton: FC<GenericButtonProps> = ({
  label,
  variant = 'contained',
  buttonType = GenericButtonTypeEnum.Primary,
  startIcon,
  endIcon,
  isActive = true,
  onClick,
  onAsyncAction,
  disabled,
  isLoading,
  sx,
  type = 'button'
}) => {
  const theme = useTheme();
  const [isActionInProcess, setIsActionInProcess] = useState(false);

  const onButtonPress = async () => {
    try {
      if (onAsyncAction) {
        setIsActionInProcess(true);
        await onAsyncAction();
        setIsActionInProcess(false);
        return;
      }

      if (onClick) {
        onClick();
      }
    } catch (error) {
      console.log(error);
      setIsActionInProcess(false);
    }
  };

  const defaultStyles: SxProps<Theme> = {
    height: 40,
    padding: '8px 22px',
    textTransform: 'none',
    justifyContent: 'center',
    flexDirection: 'row',
    alignItems: 'center',
    fontFamily: theme.typography.fontFamily,
    fontSize: 16,
    fontWeight: 400,
    borderRadius: '10px',
    cursor: 'pointer'
  };

  const typeStyles: Record<GenericButtonTypeEnum, SxProps<Theme>> = {
    [GenericButtonTypeEnum.Primary]: {
      color: palette.common.white,
      backgroundColor: isActive ? theme.palette.primary.main || palette.primary.main : theme.palette.primary.dark,
      opacity: isActive ? 1 : 0.5,
      '&:hover': {
        backgroundColor: isActive ? theme.palette.primary.main || palette.primary.main : theme.palette.primary.dark,
        boxShadow: 'none',
        opacity: 1
      }
    },
    [GenericButtonTypeEnum.Secondary]: {
      border: `1px solid ${theme.palette.primary.main}`,
      backgroundColor: 'white',
      color: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: palette.surface.hover,
        borderColor: theme.palette.primary.main
      }
    },
    [GenericButtonTypeEnum.Delete]: {
      border: `1px solid ${palette.error.main}`,
      backgroundColor: 'white',
      color: palette.error.main,
      '&:hover': {
        backgroundColor: palette.surface.hover,
        borderColor: palette.error.main
      }
    },
    [GenericButtonTypeEnum.Neutral]: {
      backgroundColor: 'transparent',
      color: theme.palette.text.primary,
      padding: '0px',
      '&:hover': {
        opacity: 0.7
      }
    }
  };
  const mergedSx: SxProps<Theme> = [defaultStyles, typeStyles[buttonType], ...(Array.isArray(sx) ? sx : sx ? [sx] : [])];

  return (
    <Button
      variant={variant}
      type={type}
      startIcon={startIcon}
      endIcon={endIcon}
      onClick={onButtonPress}
      disabled={disabled || isActionInProcess || isLoading}
      sx={mergedSx}
      loading={isActionInProcess || isLoading}
    >
      {label}
    </Button>
  );
};
export default GenericButton;
